import React from 'react';
import { Form, Input, Button, Space } from 'antd';
import { useIntl, FormattedMessage } from 'react-intl';
import PasswordStrengthBar from 'Common/Components/PasswordStrengthBar';

const { Item } = Form;
const { Password } = Input;

interface Props {
  handleSubmit: (data: FormValues) => void;
}

export interface FormValues {
  password: string;
  passwordConfirmation: string;
}

const ResetPasswordForm: React.FC<Props> = ({ handleSubmit }) => {
  const intl = useIntl();
  const [form] = Form.useForm<FormValues>();
  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={(values) => {
        handleSubmit(values);
        form.resetFields();
      }}
      css={`
        label {
          color: rgba(0, 0, 0, 0.45);
        }
        .ant-form-item-label {
          padding-bottom: 3px;
        }
      `}
    >
      <Space orientation="vertical" css="width: 100%" size="large">
        <div>
          <Item
            name="password"
            label={intl.formatMessage({ defaultMessage: 'New password' })}
            validateFirst
            rules={[
              {
                required: true,
                message: intl.formatMessage({
                  defaultMessage: 'This field is required',
                }),
              },
              {
                min: 8,
                message: intl.formatMessage(
                  {
                    defaultMessage:
                      'The password must contain at least {len} characters',
                  },
                  { len: 8 }
                ),
              },
              {
                pattern: /[a-z]/,
                message: intl.formatMessage({
                  defaultMessage:
                    'The password must contain at least one lowercase character',
                }),
              },
              {
                pattern: /[A-Z]/,
                message: intl.formatMessage({
                  defaultMessage:
                    'The password must contain at least one uppercase character',
                }),
              },
            ]}
          >
            <Password size="large" css="width: 350px" />
          </Item>
          <Item
            noStyle
            shouldUpdate={(prev, next) => prev.password !== next.password}
          >
            {() =>
              form.getFieldValue('password') && (
                <div css="margin-top: 12px">
                  <PasswordStrengthBar
                    password={form.getFieldValue('password')}
                  />
                  <span css="font-size: 11px; color: rgba(0, 0, 0, .45)">
                    <FormattedMessage defaultMessage="Password strength" />
                  </span>
                </div>
              )
            }
          </Item>
        </div>
        <Item
          name="passwordConfirmation"
          label={intl.formatMessage({
            defaultMessage: 'Password confirmation',
          })}
          dependencies={['password']}
          rules={[
            {
              required: true,
              message: intl.formatMessage({
                defaultMessage: 'This field is required',
              }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue('password') === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  intl.formatMessage({
                    defaultMessage: 'The two passwords do not match',
                  })
                );
              },
            }),
          ]}
        >
          <Password size="large" css="width: 350px" />
        </Item>
        <Item>
          <Button type="primary" size="large" htmlType="submit" block>
            <FormattedMessage defaultMessage="Reset password" />
          </Button>
        </Item>
      </Space>
    </Form>
  );
};

export default ResetPasswordForm;
